import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  BookOpen,
  Plus,
  Trash2,
  Loader2,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  X,
  CheckCircle,
  Shield,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateChapterContent, generateChapterBrief, validateOutline, grammarCheck, editorialPass, proofreadChapter, OutlineValidationResult } from '../lib/ai-config';
import { uploadFile } from '../lib/storage';
import { apiFetch } from '../lib/api';
import { FileUpload } from './FileUpload';
import { parseBookOutline } from '../lib/outline-parser';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProgressBar } from './ProgressBar';
import { OutlineDialog } from './OutlineDialog';

const toneOptions = [
  'Formal',
  'Casual',
  'Academic',
  'Conversational',
  'Professional but friendly',
  'Storytelling',
] as const;


const perspectives = [
  'First-person',
  'Second-person',
  'Third-person limited',
  'Third-person omniscient',
] as const;

const chapterTypes = ['introduction', 'chapter', 'conclusion'] as const;


const bookTypes = [
  { value: 'authority', label: 'Authority / Business Book', desc: 'Establish credibility & generate leads' },
  { value: 'educational', label: 'How-To / Educational Guide', desc: 'Teach a specific skill or topic' },
  { value: 'fiction', label: 'Fiction Novel', desc: 'Tell an entertaining story' },
  { value: 'ebook', label: 'Short eBook / Lead Magnet', desc: 'Quick guide, report, or lead magnet' },
  { value: 'children', label: "Children's Book", desc: 'Stories for young readers' },
] as const;

const chapterLengthPresets: Record<
  string,
  {
    short: number;
    average: number;
    long: number;
    shortLabel: string;
    avgLabel: string;
    longLabel: string;
  }
> = {
  authority: {
    short: 1500,
    average: 2500,
    long: 4000,
    shortLabel: 'Concise (~1,500 words)',
    avgLabel: 'Standard (~2,500 words)',
    longLabel: 'In-Depth (~4,000 words)',
  },
  educational: {
    short: 1500,
    average: 3000,
    long: 5000,
    shortLabel: 'Brief (~1,500 words)',
    avgLabel: 'Standard (~3,000 words)',
    longLabel: 'Comprehensive (~5,000 words)',
  },
  fiction: {
    short: 2000,
    average: 3500,
    long: 5000,
    shortLabel: 'Short (~2,000 words)',
    avgLabel: 'Standard (~3,500 words)',
    longLabel: 'Long (~5,000 words)',
  },
  ebook: {
    short: 500,
    average: 1000,
    long: 2000,
    shortLabel: 'Quick (~500 words)',
    avgLabel: 'Standard (~1,000 words)',
    longLabel: 'Detailed (~2,000 words)',
  },
  children: {
    short: 150,
    average: 500,
    long: 1500,
    shortLabel: 'Picture Book (~150 words)',
    avgLabel: 'Early Reader (~500 words)',
    longLabel: 'Chapter Book (~1,500 words)',
  },
};

const bookSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  author: z.string().min(1, 'Author name is required'),
  author_bio: z.string().min(1, 'Author bio is required'),
  audience: z.string().optional(),
  perspective: z.enum(perspectives),
  goals: z.string().optional(),
  disclaimers: z.string().optional(),
  rules: z.string().optional(),
  writing_style_sample: z.string().optional(),
  style_notes: z.string().optional(),
  preferred_tone: z.array(z.enum(toneOptions)).default(['Professional but friendly']),
  ai_model: z.string().default('claude-sonnet-4-6'),
  target_word_count: z.number().min(100).max(10000).default(800),
  chapters: z
    .array(
      z.object({
        chapter_title: z.string().min(1, 'Chapter title is required'),
        notes: z.string().optional(),
        type: z.enum(chapterTypes).default('chapter'),
        chapter_number: z.number().optional(),
        chapter_references: z.string().optional(),
      })
    )
    .min(1, 'Add at least one chapter'),
});

type BookFormData = z.infer<typeof bookSchema>;

const FORM_STORAGE_KEY = 'ezbookwriter-form-draft';

export function BookForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; path: string; size: number }[]
  >([]);
  const [outlineText, setOutlineText] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number; status?: string } | null>(null);
  const [showOutlineDialog, setShowOutlineDialog] = useState(false);
  const [bookType, setBookType] = useState<string>('');
  const [showManualOutline, setShowManualOutline] = useState(false);
  const [outlineSource, setOutlineSource] = useState<'generated' | 'pasted' | null>(null);
  const [chapterLengthPreset, setChapterLengthPreset] = useState<
    'short' | 'average' | 'long' | 'custom' | null
  >(null);

  // Outline validation state
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<OutlineValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationPassed, setValidationPassed] = useState(false);

  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const [manualReferences, setManualReferences] = useState('');
  const [perplexityAvailable, setPerplexityAvailable] = useState(false);
  const [chapterResearchEnabled, setChapterResearchEnabled] = useState<Record<number, boolean>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
  } = useForm<BookFormData>({
    resolver: zodResolver(bookSchema),
    defaultValues: {
      chapters: [{ chapter_title: '', notes: '', type: 'chapter', chapter_number: 1 }],
      perspective: 'Third-person limited',
      ai_model: 'claude-sonnet-4-6',
      preferred_tone: ['Professional but friendly'],
      target_word_count: 0,
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'chapters',
  });

  // Guard to prevent saving default values before restore completes
  const [formLoaded, setFormLoaded] = useState(false);

  // Check if Perplexity research is available
  React.useEffect(() => {
    apiFetch('/api/perplexity-available')
      .then(res => res.json())
      .then(data => setPerplexityAvailable(data.available))
      .catch(() => setPerplexityAvailable(false));
  }, []);

  // Load saved form data from sessionStorage on mount
  React.useEffect(() => {
    try {
      const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.formData) {
          reset(parsed.formData);
        }
        if (parsed.outlineText) {
          setOutlineText(parsed.outlineText);
        }
        if (parsed.bookType) {
          setBookType(parsed.bookType);
        }
        if (parsed.chapterLengthPreset) {
          setChapterLengthPreset(parsed.chapterLengthPreset);
        }
      }
    } catch (e) {
      console.error('Error loading saved form data:', e);
    }
    setFormLoaded(true);
  }, []);

  // Save form data to sessionStorage whenever it changes (only after initial load)
  const watchedValues = watch();

  React.useEffect(() => {
    if (!formLoaded) return;
    try {
      const dataToSave = {
        formData: watchedValues,
        outlineText,
        bookType,
        chapterLengthPreset,
      };
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
      console.error('Error saving form data:', e);
    }
  }, [watchedValues, outlineText, bookType, chapterLengthPreset, formLoaded]);

  const handleFileUpload = async (files: File[]) => {
    if (!user) return;
    setIsUploading(true);
    try {
      for (const file of files) {
        const uploadedFile = await uploadFile(file, user.id);
        setUploadedFiles((prev) => [...prev, uploadedFile]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileRemove = async (path: string) => {
    try {
      const { error } = await supabase.storage.from('book-files').remove([path]);
      if (error) throw error;
      setUploadedFiles((prev) => prev.filter((file) => file.path !== path));
    } catch (error) {
      console.error('Error removing file:', error);
      alert('Error removing file. Please try again.');
    }
  };

  const handleOutlineGenerated = (outline: string) => {
    setOutlineText(outline);
    setShowOutlineDialog(false);
    setOutlineSource('generated');
    setValidationError(null);

    // Our outline passed pre-flight viability — trust it, parse and approve
    try {
      const parsed = parseBookOutline(outline);
      if (parsed.chapters.length > 0) {
        const currentTitle = watch('title');
        if (parsed.title && !currentTitle) {
          setValue('title', parsed.title);
        }
        const newChapters = parsed.chapters.map((ch, i) => ({
          chapter_title: ch.title,
          notes: ch.content.join('\n'),
          type: ch.type as 'introduction' | 'chapter' | 'conclusion',
          chapter_number: ch.chapter_number || (ch.type === 'chapter' ? i : undefined),
        }));
        setValue('chapters', newChapters);
        setValidationResult({ approved: true, overallMessage: 'Outline approved.', chapterFeedback: [] });
        setValidationPassed(true);
      }
    } catch (error) {
      console.error('Error auto-parsing outline:', error);
      // Fall back to manual flow if parse fails
      setValidationResult(null);
      setValidationPassed(false);
      setOutlineSource('pasted');
    }
  };

  const handleAddChapter = () => {
    const currentChapters = fields.filter((f) => f.type === 'chapter');
    const nextChapterNumber =
      currentChapters.length > 0
        ? Math.max(...currentChapters.map((c) => c.chapter_number || 0)) + 1
        : 1;
    append({ chapter_title: '', notes: '', type: 'chapter', chapter_number: nextChapterNumber });
  };

  const handleValidateOutline = async () => {
    if (!outlineText.trim()) {
      setValidationError('Generate or paste an outline first.');
      return;
    }

    const targetWordCount = watch('target_word_count');

    // Temporarily parse outline to extract chapters for validation
    let parsedChapters;
    try {
      const parsed = parseBookOutline(outlineText);
      if (parsed.chapters.length === 0) {
        setValidationError('Could not find any chapters in the outline. Check the format and try again.');
        return;
      }
      parsedChapters = parsed.chapters.map((ch) => ({
        chapter_title: ch.title,
        notes: ch.content.join('\n'),
        type: ch.type,
      }));
    } catch {
      setValidationError('Could not parse the outline. Check the format and try again.');
      return;
    }

    setIsValidating(true);
    setValidationError(null);
    setValidationResult(null);
    setValidationPassed(false);

    try {
      const result = await validateOutline({
        chapters: parsedChapters,
        targetWordCount,
        bookType,
      });
      setValidationResult(result);
      if (result.approved) {
        setValidationPassed(true);
        // Now parse into the form since validation passed
        const parsed = parseBookOutline(outlineText);
        const currentTitle = watch('title');
        if (parsed.title && !currentTitle) {
          setValue('title', parsed.title);
        }
        const newChapters = parsed.chapters.map((ch, i) => ({
          chapter_title: ch.title,
          notes: ch.content.join('\n'),
          type: ch.type as 'introduction' | 'chapter' | 'conclusion',
          chapter_number: ch.chapter_number || (ch.type === 'chapter' ? i : undefined),
        }));
        setValue('chapters', newChapters);
      }
    } catch (err) {
      console.error('Validation error:', err);
      setValidationError(err instanceof Error ? err.message : 'Failed to validate outline');
    } finally {
      setIsValidating(false);
    }
  };

  const onSubmit = async (data: BookFormData) => {
    if (!user) {
      setError('You must be logged in to create a book');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setProgress({ current: 0, total: data.chapters.length });

    try {
      const { data: book, error: bookError } = await supabase
        .from('books_metadata')
        .insert({
          user_id: user.id,
          title: data.title,
          subtitle: data.subtitle,
          author: data.author,
          author_bio: data.author_bio,
          audience: data.audience,
          perspective: data.perspective,
          goals: data.goals,
          disclaimers: data.disclaimers,
          rules: data.rules,
          writing_style_sample: data.writing_style_sample,
          style_notes: data.style_notes,
          preferred_tone: data.preferred_tone,
          ai_model: data.ai_model,
          target_word_count: data.target_word_count,
          outline_text: outlineText || null,
        })
        .select()
        .single();

      if (bookError) throw bookError;

      // Build research content from uploaded files via server-side extraction
      let researchContent = '';
      if (uploadedFiles.length > 0) {
        try {
          const fileContents: string[] = [];
          for (const file of uploadedFiles) {
            try {
              // Download file from Supabase (client has auth)
              const { data: fileData, error: fileError } = await supabase.storage
                .from('book-files')
                .download(file.path);
              if (fileError || !fileData) {
                console.error(`Error downloading research file ${file.name}:`, fileError);
                continue;
              }

              // For TXT files, extract directly on client
              const ext = file.name.split('.').pop()?.toLowerCase() || '';
              if (ext === 'txt') {
                const text = await fileData.text();
                if (text.trim()) {
                  fileContents.push(`--- Research File: ${file.name} ---\n${text.trim()}`);
                }
                continue;
              }

              // For PDF/DOCX, send to server for extraction
              const arrayBuffer = await fileData.arrayBuffer();
              const base64 = btoa(
                new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
              );

              const extractRes = await apiFetch('/api/extract-file-text', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileBase64: base64, filename: file.name }),
              });
              if (!extractRes.ok) {
                console.error(`Error extracting text from ${file.name}: HTTP ${extractRes.status}`);
                continue;
              }
              const { text } = await extractRes.json();
              if (text && text.trim()) {
                fileContents.push(`--- Research File: ${file.name} ---\n${text.trim()}`);
              }
            } catch (err) {
              console.error(`Error extracting research file ${file.name}:`, err);
            }
          }
          if (fileContents.length > 0) {
            researchContent = fileContents.join('\n\n');
          }
        } catch (err) {
          console.error('Error extracting research file content:', err);
        }
      }

      // Track prior chapter openings, briefs, and character names to prevent repetition/collisions
      const priorChapterOpenings: string[] = [];
      const priorChapterBriefs: string[] = [];
      const usedCharacterNames: string[] = [];
      const allChapterTitles = data.chapters.map((ch) => {
        if (ch.type === 'chapter' && ch.chapter_number) {
          return `Chapter ${ch.chapter_number}: ${ch.chapter_title}`;
        }
        return ch.chapter_title;
      });

      for (let i = 0; i < data.chapters.length; i++) {
        const chapter = data.chapters[i];

        try {
          const chapterNumber = chapter.type === 'chapter' ? chapter.chapter_number : null;
          const chapterTitle =
            chapter.type === 'chapter' && chapterNumber
              ? `Chapter ${chapterNumber}: ${chapter.chapter_title}`
              : chapter.chapter_title;

          // Step 1: Generate a detailed chapter brief/outline
          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Planning "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });
          // Yield to event loop so React can render the progress update
          await new Promise((r) => setTimeout(r, 0));

          const chapterBrief = await generateChapterBrief({
            bookTitle: data.title,
            chapterTitle,
            chapterType: chapter.type,
            chapterNotes: chapter.notes,
            audience: data.audience || '',
            perspective: data.perspective,
            goals: data.goals || '',
            target_word_count: data.target_word_count,
            bookType,
            researchContent,
            priorChapterBriefs,
            citationsEnabled,
            manualReferences,
            chapterReferences: chapter.chapter_references,
          });

          // Track this brief so future chapters don't repeat content
          priorChapterBriefs.push(`${chapterTitle}: ${chapterBrief.slice(0, 300)}`);

          // Step 1.5: AI Research (optional, per-chapter)
          let chapterResearchContent = researchContent;
          if (chapterResearchEnabled[i]) {
            setProgress({
              current: i + 1,
              total: data.chapters.length,
              status: `Researching "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
            });
            await new Promise((r) => setTimeout(r, 0));

            try {
              const researchRes = await apiFetch('/api/research-chapter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  bookTitle: data.title,
                  chapterTitle,
                  chapterBrief,
                  audience: data.audience || '',
                }),
              });

              if (researchRes.ok) {
                const researchData = await researchRes.json();
                // Append research to existing research content
                if (researchData.research) {
                  chapterResearchContent = (chapterResearchContent ? chapterResearchContent + '\n\n' : '') +
                    `--- AI Research for ${chapterTitle} ---\n${researchData.research}`;
                }
                // Auto-populate chapter references with citations in Chicago style
                if (researchData.citations && researchData.citations.length > 0) {
                  const existingRefs = chapter.chapter_references || '';
                  const newRefs = researchData.citations
                    .map((url: string) => `"${chapterTitle} Research." ${url}`)
                    .join('\n');
                  const combined = existingRefs ? existingRefs + '\n' + newRefs : newRefs;
                  setValue(`chapters.${i}.chapter_references`, combined);
                  chapter.chapter_references = combined;
                }
              }
            } catch (err) {
              console.error(`[research] Error researching "${chapterTitle}":`, err);
              // Research is optional — continue without it
            }
          }

          // Step 2: Generate full chapter content following the brief
          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Writing "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });
          await new Promise((r) => setTimeout(r, 0));

          const generatedContent = await generateChapterContent({
            model: data.ai_model,
            bookTitle: data.title,
            audience: data.audience || '',
            perspective: data.perspective,
            goals: data.goals || '',
            disclaimers: data.disclaimers,
            rules: data.rules || '',
            writingStyleSample: data.writing_style_sample,
            styleNotes: data.style_notes,
            preferredTone: data.preferred_tone,
            chapterTitle,
            chapterType: chapter.type,
            chapterNotes: chapter.notes,
            target_word_count: data.target_word_count,
            bookType,
            researchContent: chapterResearchContent,
            priorChapterOpenings,
            chapterBrief,
            citationsEnabled,
            manualReferences,
            chapterReferences: chapter.chapter_references,
          });

          // Step 3: Three-pass polish pipeline — grammar → editorial → proofread
          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Grammar check "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });
          await new Promise((r) => setTimeout(r, 0));

          // Pass A: Grammar check via LanguageTool
          const { correctedText: grammarCorrected } = await grammarCheck(generatedContent);

          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Editorial pass "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });
          await new Promise((r) => setTimeout(r, 0));

          // Pass B: Editorial pass via Claude
          const editorialCorrected = await editorialPass({
            text: grammarCorrected,
            bookTitle: data.title,
            chapterTitle,
            priorChapterSummaries: priorChapterBriefs,
          });

          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Proofreading "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });
          await new Promise((r) => setTimeout(r, 0));

          // Pass C: Proofread — fix vague headings, name collisions, broken refs
          const polishedContent = await proofreadChapter({
            content: editorialCorrected,
            bookTitle: data.title,
            chapterTitle,
            chapterType: chapter.type,
            allChapterTitles,
            previouslyUsedNames: usedCharacterNames,
          });

          // Extract character/person names from the polished content for collision detection
          // Simple heuristic: find capitalized two-word names that appear near common intro patterns
          const namePattern = /(?:named|called|meet|introduces?|friend|neighbor|client|patient|colleague)\s+([A-Z][a-z]+)/g;
          let nameMatch;
          while ((nameMatch = namePattern.exec(polishedContent)) !== null) {
            const name = nameMatch[1];
            if (!usedCharacterNames.includes(name)) {
              usedCharacterNames.push(name);
            }
          }

          // Extract the first ~50 words as the opening for future chapters
          const opening = polishedContent.trim().split(/\s+/).slice(0, 50).join(' ');
          priorChapterOpenings.push(`${chapterTitle}: ${opening}`);

          setProgress({
            current: i + 1,
            total: data.chapters.length,
            status: `Saving "${chapterTitle}" (${i + 1} of ${data.chapters.length})...`,
          });

          const { error: chapterError } = await supabase.from('books_chapters').insert({
            book_id: book.book_id,
            chapter_number: chapterNumber,
            chapter_title: chapter.chapter_title,
            notes: chapter.notes,
            generated_text: polishedContent,
            status: 'completed',
            chapter_type: chapter.type,
            order_index: i,
            chapter_references: chapter.chapter_references || null,
          });

          if (chapterError) throw chapterError;
        } catch (error) {
          console.error(`Error generating chapter "${chapter.chapter_title}":`, error);
          throw new Error(`Failed to generate chapter "${chapter.chapter_title}"`);
        }
      }

      sessionStorage.removeItem(FORM_STORAGE_KEY);
      navigate(`/books/${book.book_id}`);
    } catch (error) {
      console.error('Error creating book:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsGenerating(false);
      setProgress(null);
    }
  };

  const targetWordCount = watch('target_word_count');

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Create New Book</h1>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </div>
            </div>

            {progress && (
              <div className="mb-8">
                <ProgressBar current={progress.current} total={progress.total} status={progress.status} />
              </div>
            )}

            {/* Book Type & Chapter Length — shown first so outline generation uses the right settings */}
            <div className="space-y-6 mb-8">
              <h2 className="text-lg font-medium text-gray-900">Book Type & Chapter Length</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What type of book are you creating?
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {bookTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        setBookType(type.value);
                        const presets = chapterLengthPresets[type.value];
                        if (chapterLengthPreset && chapterLengthPreset !== 'custom') {
                          setValue('target_word_count', presets[chapterLengthPreset]);
                        }
                        // Clear validation when book type changes
                        setValidationResult(null);
                        setValidationPassed(false);
                      }}
                      className={`relative rounded-lg border p-4 text-left transition-all ${
                        bookType === type.value
                          ? 'border-indigo-500 ring-2 ring-indigo-500 bg-indigo-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {bookType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Chapter Length
                </label>
                <div className="flex flex-wrap gap-3">
                  {(['short', 'average', 'long', 'custom'] as const).map((preset) => {
                    const presets = chapterLengthPresets[bookType];
                    const label =
                      preset === 'short'
                        ? presets.shortLabel
                        : preset === 'average'
                          ? presets.avgLabel
                          : preset === 'long'
                            ? presets.longLabel
                            : 'Custom';
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setChapterLengthPreset(preset);
                          if (preset !== 'custom') {
                            setValue('target_word_count', presets[preset]);
                          }
                          // Clear validation when word count changes
                          setValidationResult(null);
                          setValidationPassed(false);
                        }}
                        className={`px-4 py-3 rounded-lg border text-sm transition-all ${
                          chapterLengthPreset === preset
                            ? 'border-indigo-500 ring-2 ring-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-300 hover:border-gray-400 text-gray-700'
                        }`}
                      >
                        <div className="font-medium capitalize">{preset}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                      </button>
                    );
                  })}
                </div>
                {chapterLengthPreset === 'custom' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Words per chapter
                    </label>
                    <input
                      type="number"
                      {...register('target_word_count', { valueAsNumber: true })}
                      className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      min={100}
                      max={10000}
                      onChange={(e) => {
                        setValue('target_word_count', Number(e.target.value));
                        setValidationResult(null);
                        setValidationPassed(false);
                      }}
                    />
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Book Outline — generate or paste, only after type & length chosen */}
            {bookType && chapterLengthPreset && (
            <div className="space-y-6 border-t border-gray-200 pt-6 mb-8">
              <h2 className="text-lg font-medium text-gray-900">Book Outline</h2>
              <p className="text-sm text-gray-500">
                An outline defines your book's chapter structure. You can generate one automatically or paste your own.
              </p>
              <div className="space-y-4">
                {!outlineText ? (
                  <>
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setShowOutlineDialog(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Generate Outline
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowManualOutline(!showManualOutline)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {showManualOutline ? 'Hide' : 'Paste Your Own'}
                      </button>
                    </div>
                    {showManualOutline && (
                      <textarea
                        value={outlineText}
                        onChange={(e) => {
                          setOutlineText(e.target.value);
                          setOutlineSource('pasted');
                        }}
                        rows={6}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder="Paste your book outline here..."
                      />
                    )}
                  </>
                ) : (
                  <>
                    <textarea
                      value={outlineText}
                      onChange={(e) => {
                        setOutlineText(e.target.value);
                        if (outlineSource === 'pasted') {
                          setValidationResult(null);
                          setValidationPassed(false);
                        }
                      }}
                      rows={12}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm"
                    />
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setShowOutlineDialog(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Regenerate Outline
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOutlineText('');
                          setOutlineSource(null);
                          setValidationResult(null);
                          setValidationPassed(false);
                          setValue('chapters', [{ chapter_title: '', notes: '', type: 'chapter', chapter_number: 1 }]);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear
                      </button>
                      {/* Validate button only for user-pasted outlines */}
                      {outlineSource === 'pasted' && !validationPassed && (
                        <button
                          type="button"
                          onClick={handleValidateOutline}
                          disabled={isValidating || !outlineText.trim()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isValidating ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Validating...
                            </>
                          ) : (
                            <>
                              <Shield className="h-4 w-4 mr-2" />
                              Validate Outline
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Generated outline: clean approval message */}
                    {outlineSource === 'generated' && validationPassed && (
                      <div className="flex items-start text-green-700 bg-green-50 rounded-md p-3 text-sm">
                        <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="font-medium">Outline approved — chapters have been created below.</span>
                      </div>
                    )}

                    {/* Pasted outline: validation feedback */}
                    {outlineSource === 'pasted' && (
                      <>
                        {validationError && (
                          <div className="flex items-start text-red-700 bg-red-50 rounded-md p-3 text-sm">
                            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span>{validationError}</span>
                          </div>
                        )}

                        {validationResult && validationResult.approved && (
                          <div className="flex items-start text-green-700 bg-green-50 rounded-md p-3 text-sm">
                            <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="font-medium">Outline approved — chapters have been created below.</span>
                          </div>
                        )}

                        {validationResult && !validationResult.approved && (
                          <div className="space-y-3">
                            <div className="flex items-start text-amber-700 bg-amber-50 rounded-md p-3 text-sm">
                              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="font-medium">Outline Needs Improvement</div>
                                <div className="mt-1">{validationResult.overallMessage}</div>
                              </div>
                            </div>

                            {validationResult.chapterFeedback &&
                              validationResult.chapterFeedback.some((ch) => ch.status !== 'ok') && (
                                <div className="bg-white rounded-md border border-gray-200 divide-y divide-gray-100">
                                  {validationResult.chapterFeedback
                                    .filter((ch) => ch.status !== 'ok')
                                    .map((ch, idx) => (
                                      <div key={idx} className="px-3 py-2 text-sm">
                                        <div className="flex items-center">
                                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${ch.status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`} />
                                          <span className="font-medium text-gray-900">{ch.title}</span>
                                        </div>
                                        {ch.suggestion && (
                                          <div className="ml-4 mt-1 text-gray-600">{ch.suggestion}</div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              )}

                            {!validationPassed && (
                              <div className="bg-red-50 border border-red-200 rounded-md p-4 space-y-3">
                                <p className="text-sm text-red-800 font-medium">
                                  Your outline does not support {targetWordCount.toLocaleString()} words/chapter. You can edit the outline above, or:
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const reduced = Math.max(300, Math.floor(targetWordCount * 0.5));
                                      setValue('target_word_count', reduced);
                                      setChapterLengthPreset('custom');
                                      setValidationPassed(true);
                                      try {
                                        const parsed = parseBookOutline(outlineText);
                                        if (parsed.chapters.length > 0) {
                                          const currentTitle = watch('title');
                                          if (parsed.title && !currentTitle) setValue('title', parsed.title);
                                          setValue('chapters', parsed.chapters.map((ch, i) => ({
                                            chapter_title: ch.title,
                                            notes: ch.content.join('\n'),
                                            type: ch.type as 'introduction' | 'chapter' | 'conclusion',
                                            chapter_number: ch.chapter_number || (ch.type === 'chapter' ? i : undefined),
                                          })));
                                        }
                                      } catch (e) { console.error('Parse error:', e); }
                                    }}
                                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Reduce to {Math.max(300, Math.floor(targetWordCount * 0.5)).toLocaleString()} words & proceed
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm(
                                        'Your outline may produce thin content at this word count.\n\nAre you sure you want to proceed?'
                                      )) {
                                        setValidationPassed(true);
                                        try {
                                          const parsed = parseBookOutline(outlineText);
                                          if (parsed.chapters.length > 0) {
                                            const currentTitle = watch('title');
                                            if (parsed.title && !currentTitle) setValue('title', parsed.title);
                                            setValue('chapters', parsed.chapters.map((ch, i) => ({
                                              chapter_title: ch.title,
                                              notes: ch.content.join('\n'),
                                              type: ch.type as 'introduction' | 'chapter' | 'conclusion',
                                              chapter_number: ch.chapter_number || (ch.type === 'chapter' ? i : undefined),
                                            })));
                                          }
                                        } catch (e) { console.error('Parse error:', e); }
                                      }
                                    }}
                                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-red-700 bg-white border border-red-300 hover:bg-red-50"
                                  >
                                    <AlertCircle className="h-4 w-4 mr-2" />
                                    Proceed anyway
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            )}

            {/* Everything below only after validation passes */}
            {validationPassed && (<>

            {/* Book Metadata */}
            <div className="space-y-6 border-t border-gray-200 pt-6 mb-8">
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700"
                >
                  Book Title
                </label>
                <input
                  type="text"
                  {...register('title')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="subtitle"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subtitle (Optional)
                </label>
                <input
                  type="text"
                  {...register('subtitle')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label
                  htmlFor="author"
                  className="block text-sm font-medium text-gray-700"
                >
                  Author Name
                </label>
                <input
                  type="text"
                  {...register('author')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                {errors.author && (
                  <p className="mt-1 text-sm text-red-600">{errors.author.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="author_bio"
                  className="block text-sm font-medium text-gray-700"
                >
                  About the Author
                </label>
                <textarea
                  {...register('author_bio')}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Write a compelling biography that will appear in your book"
                />
                {errors.author_bio && (
                  <p className="mt-1 text-sm text-red-600">{errors.author_bio.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-6 border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Chapters</h2>
                <button
                  type="button"
                  onClick={handleAddChapter}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Chapter
                </button>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 mr-4">
                        <label className="block text-sm font-medium text-gray-700">
                          Chapter Type
                        </label>
                        <select
                          {...register(`chapters.${index}.type`)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          {chapterTypes.map((type) => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => move(index, index - 1)}
                            className="p-1 text-gray-400 hover:text-gray-500"
                          >
                            <ChevronUp className="h-5 w-5" />
                          </button>
                        )}
                        {index < fields.length - 1 && (
                          <button
                            type="button"
                            onClick={() => move(index, index + 1)}
                            className="p-1 text-gray-400 hover:text-gray-500"
                          >
                            <ChevronDown className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            remove(index);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {field.type === 'chapter' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Chapter Number
                          </label>
                          <input
                            type="number"
                            {...register(`chapters.${index}.chapter_number` as const, {
                              valueAsNumber: true,
                            })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            min={1}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Title
                        </label>
                        <input
                          type="text"
                          {...register(`chapters.${index}.chapter_title`)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        {errors.chapters?.[index]?.chapter_title && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.chapters[index]?.chapter_title?.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <textarea
                          {...register(`chapters.${index}.notes`)}
                          rows={3}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          placeholder="Add any notes or outline for this chapter..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Chapter References
                        </label>
                        <textarea
                          {...register(`chapters.${index}.chapter_references`)}
                          rows={2}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                          placeholder="Add references for this chapter (author, title, year, URL — one per line)"
                        />
                      </div>

                      {perplexityAvailable && (
                        <div>
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={!!chapterResearchEnabled[index]}
                              onChange={(e) => setChapterResearchEnabled(prev => ({ ...prev, [index]: e.target.checked }))}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700">Research with AI</span>
                              <p className="text-xs text-gray-500">Use Perplexity to find relevant facts and citations for this chapter</p>
                            </div>
                          </label>
                          {chapterResearchEnabled[index] && (
                            <p className="mt-1 text-xs text-amber-600">AI research supplements your expertise. Always verify citations before publishing.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {fields.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No chapters added yet. Click "Add Chapter" to get started.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-6 border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Advanced Settings</h2>
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {showAdvancedOptions ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Hide Advanced Options
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Show Advanced Options
                    </>
                  )}
                </button>
              </div>

              <div className={`space-y-6 ${showAdvancedOptions ? '' : 'hidden'}`}>
                <div>
                  <label
                    htmlFor="audience"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Target Audience
                  </label>
                  <input
                    type="text"
                    {...register('audience')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="e.g., Business owners, Students, General readers"
                  />
                </div>

                <div>
                  <label
                    htmlFor="perspective"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Writing Perspective
                  </label>
                  <select
                    {...register('perspective')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {perspectives.map((perspective) => (
                      <option key={perspective} value={perspective}>
                        {perspective}
                      </option>
                    ))}
                  </select>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Preferred Tone
                  </label>
                  <div className="mt-2 space-y-2">
                    {toneOptions.map((tone) => (
                      <label key={tone} className="inline-flex items-center mr-4">
                        <input
                          type="checkbox"
                          value={tone}
                          {...register('preferred_tone')}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{tone}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="goals"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Book Goals
                  </label>
                  <textarea
                    {...register('goals')}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="What are the main objectives of this book?"
                  />
                </div>

                <div>
                  <label
                    htmlFor="disclaimers"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Disclaimers
                  </label>
                  <textarea
                    {...register('disclaimers')}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Any disclaimers or warnings to include"
                  />
                </div>

                <div>
                  <label
                    htmlFor="rules"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Writing Rules
                  </label>
                  <textarea
                    {...register('rules')}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Specific rules or guidelines for writing"
                  />
                </div>

                <div>
                  <label
                    htmlFor="writing_style_sample"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Writing Style Sample
                  </label>
                  <textarea
                    {...register('writing_style_sample')}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Example of the desired writing style"
                  />
                </div>

                <div>
                  <label
                    htmlFor="style_notes"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Style Notes
                  </label>
                  <textarea
                    {...register('style_notes')}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Additional notes about the writing style"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6 border-t border-gray-200 pt-6">
              <h2 className="text-lg font-medium text-gray-900">Research & References</h2>
              <FileUpload
                onUpload={handleFileUpload}
                isUploading={isUploading}
                uploadedFiles={uploadedFiles}
                onRemove={handleFileRemove}
              />

              <div className="mt-4 space-y-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={citationsEnabled}
                    onChange={(e) => setCitationsEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Include formal citations & references</span>
                    <p className="text-xs text-gray-500">Cite sources with author/year in-text and add a references section to each chapter</p>
                  </div>
                </label>

                <div>
                  <label htmlFor="manual_references" className="block text-sm font-medium text-gray-700">
                    Manual References
                  </label>
                  <p className="text-xs text-gray-500 mb-1">
                    Paste citation details for sources you want cited (author, title, year, URL). One per line.
                  </p>
                  <textarea
                    id="manual_references"
                    value={manualReferences}
                    onChange={(e) => setManualReferences(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    placeholder="Smith, J. (2023). Study on AI in Healthcare. Journal of AI Research. https://example.com&#10;World Health Organization. (2024). Global Health Report."
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 mt-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isGenerating || !validationPassed}
                  title={!validationPassed ? 'Validate your outline before generating' : undefined}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Generating Book...
                      {progress && (
                        <span className="ml-2">
                          ({progress.current}/{progress.total})
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Generate Book
                    </>
                  )}
                </button>
              </div>
              {!validationPassed && fields.length > 0 && !isGenerating && (
                <p className="mt-2 text-sm text-gray-500 text-right">
                  Validate your outline above before generating.
                </p>
              )}
            </div>
            </>)}
          </div>
        </form>
      </div>

      <OutlineDialog
        isOpen={showOutlineDialog}
        onClose={() => setShowOutlineDialog(false)}
        onOutlineGenerated={handleOutlineGenerated}
        bookType={bookType}
        targetWordCount={watch('target_word_count')}
      />
    </div>
  );
}